import * as d3 from 'd3';
import { Kpi, KpiWithOptions } from './Kpi';
import { DisplayKpi } from './DisplayKpi';
import { RoundingHelper, LoggerFactory } from '../../helpers/helpers';
import { Symbols } from '../../models/Symbols';

const roundingHelper = new RoundingHelper();
const _format = d3.format('2.2f');

const _base = '204,204,204';
const _logger = LoggerFactory.getLogger('BulletChartHelper');

export const light = `rgba(${_base},0.33)`;
export const dark = `rgba(${_base},0.66)`;

export function getGlobalMinMax(kpi: Kpi): any {
    const result = { min: 0, max: 1 };
    if (!kpi) {
        console.error('No kpi provided');
        return result;
    } try {
        const hasGlobalMin = (kpi.globalMin || kpi.globalMin === 0) && !isNaN(kpi.globalMin);
        const hasGlobalMax = kpi.globalMax || kpi.globalMax === 0 && !isNaN(kpi.globalMax);
        const min = hasGlobalMin ? kpi.globalMin : getMin(kpi);
        let max = hasGlobalMax ? kpi.globalMax : getMax(kpi);
        if (min === max) {
            max = min + 1;
        }
        const rounded = roundingHelper.round(min, max);
        if (hasGlobalMin) {
            rounded.min = min;
        }
        if (hasGlobalMax) {
            rounded.max = max;
        }
        return rounded;
    } catch (e) {
        _logger.error(e);
        return result;
    }
}

function getMax(kpi: Kpi) {
    let max: number;
    const tmpMax = kpi.value * 1.1;
    const autoMax = (kpi.target || kpi.target === 0) ? Math.max(kpi.target, tmpMax) : tmpMax;
    if (kpi.max || kpi.max === 0) {
        max = Math.max(autoMax, kpi.max);
    } else {
        max = autoMax;
    }
    return max;
}

function getMin(kpi: Kpi) {
    let min: number;
    const tmpMin = kpi.value * 0.9;
    const autoMin = (kpi.target || kpi.target === 0) ? Math.min(kpi.target, tmpMin) : tmpMin;
    if (kpi.min || kpi.min === 0) {
        min = Math.min(autoMin, kpi.min);
    } else {
        min = autoMin;
    }
    return min;
}

export function getRanges(kpi: Kpi): any[] {
    const ranges = [];
    const globalRange = getGlobalMinMax(kpi);
    const min = globalRange.min;
    const max = globalRange.max;
    let kpiMin = kpi.min;
    let kpiMax = kpi.max;
    if (kpiMin === null || kpiMin === undefined) {
        kpiMin = min;
    } else {
        kpiMin = Math.max(min, kpiMin);
    }
    if (kpiMax === null || kpiMax === undefined) {
        kpiMax = max;
    } else {
        kpiMax = Math.min(max, kpiMax);
    }
    if (kpiMax < kpiMin) {
        kpiMax = kpiMin;
    }
    ranges.push({ min: min, max: kpiMin });
    ranges.push({ min: kpiMin, max: kpiMax });
    ranges.push({ min: kpiMax, max: max });
    return ranges;
}
export function getColor(kpi: Kpi): { backColor: string; axisColor: string; } {
    const result: any = {};
    if (kpi.coloringConditions && kpi.coloringConditions.length > 0) {
        const matchedConditions = kpi.coloringConditions.filter(cc => cc.condition(kpi));
        if (matchedConditions.length > 0) {
            const matchedCondition = matchedConditions[0];
            if (matchedCondition) {
                result.backColor = matchedCondition.colorBack ? matchedCondition.colorBack : null;
                result.axisColor = matchedCondition.colorAxis;
            }

        }
    }
    return result;
}


export function toDisplayKpi(d: KpiWithOptions, plotWidth: number): DisplayKpi {
    const globalMinMax = getGlobalMinMax(d);
    const scale = d3.scale.linear<number, number>()
        .domain([globalMinMax.min, globalMinMax.max])
        .range([0, plotWidth]);
    const axis = d3.svg.axis()
        .orient('bottom')
        .scale(scale);

    const fv = getFormattedValue(d);
    const options = getOptions(d);
    const ranges = getRanges(d);
    const colors = getColor(d);
    const result: DisplayKpi = {
        scale,
        axis,
        ranges: ranges,
        target: d.target,
        formattedValue: fv,
        options: options,
        backColor: colors.backColor ? colors.backColor : dark,
        axisColor: colors.axisColor ? colors.axisColor : null,
        color: d.color,
        value: d.value,
        attributes: d.attributes,
        onClick: (d as any).onClick,
        originalObject: d
    };
    return result;
}

function getFormattedValue(d: KpiWithOptions): string {
    const valueFormat: (d: any) => string = (d.options && d.options.valueFormat)
        ? d.options.valueFormat
        : _format;
    const isValueAvailable = d.value !== null && d.value !== undefined;
    let displayValue: string = '-';
    if (isValueAvailable) {
        displayValue = valueFormat(d as any);
        if (displayValue === 'NaN') {
            displayValue = valueFormat(d.value);
        }
    }
    let unit = '';
    if (d.unit) {
        unit = ` ${d.unit}`;
    }
    const fv = `${d.name}: ${displayValue} ${unit}`;
    return fv;
}

function getOptions(d: KpiWithOptions): any[] {
    const options: any[] = [];
    if (d.options) {
        if (d.options.isTendencyVisible) {
            options.push({
                value: d3.format('2.0%')(d.variation),
                symbolPath: d.variation > 0
                    ? d3.svg.symbol().type(Symbols.triangleUp)
                    : d3.svg.symbol().type(Symbols.triangleDown),
                textAnchor: 'end'
            });
        }
        if (d.options.isQualityVisible) {
            options.push({
                value: d.quality,
                textAnchor: 'start'
            });
        }
        if (d.options.isContextVisible) {
            options.push({
                value: d.context,
                textAnchor: 'start'
            });
        }
    }
    return options;
}
